import BackspaceIcon from "@mui/icons-material/Backspace";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Checkbox, Chip, SxProps, Theme, Typography } from "@mui/material";

import { getDaysOfWeek } from "../../Configuration/utils";
import { CustomAutocomplete } from "./CustomAutocomplete";

type DayOption = { value: string; label: string };

interface BaseProps {
  label?: string;
  size?: "small" | "medium";
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
}

interface MultipleProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
  limitTags?: number;
}

type Props = SingleProps | MultipleProps;

const multipleFixedHeightSx = {
  "& .MuiAutocomplete-inputRoot": {
    flexWrap: "nowrap" as const,
    height: 40,
    overflow: "hidden",
  },
};

const chipSx = {
  flexShrink: 0,
  height: "unset",
  borderRadius: "6px",
  p: 0,
  "& .MuiChip-deleteIcon": { width: 12, fontSize: 16 },
  "& .MuiChip-label": { padding: "0px 12px 0px 8px" },
} as const;

const inputStyles = (theme: Theme) => ({
  "& .MuiInputBase-root": {
    backgroundColor: theme.vars.palette.background.input,
  },
});

const WeekDaySelector = ({
  label,
  size = "small",
  disabled,
  sx,
  ...rest
}: Props) => {
  const options: DayOption[] = Object.entries(getDaysOfWeek()).map(
    ([value, dayLabel]) => ({ value, label: dayLabel })
  );

  const combinedSx: SxProps<Theme> = [
    inputStyles,
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];

  if (rest.multiple === true) {
    const selected = options.filter((o) => rest.value.includes(o.value));

    return (
      <CustomAutocomplete<DayOption, true>
        multiple
        formStyle={false}
        disableCloseOnSelect
        limitTags={rest.limitTags}
        options={options}
        value={selected}
        getOptionKey={(o) => o.value}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        filterOptions={(opts) => opts}
        onChange={(_, newValue) => rest.onChange(newValue.map((o) => o.value))}
        renderOption={(props, option, { selected: isSelected }) => (
          <li {...props} key={option.value}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              style={{ marginRight: 8, padding: 0 }}
              checked={isSelected}
            />
            {option.label}
          </li>
        )}
        renderValue={(value, getItemProps) => {
          const limit = rest.limitTags ?? value.length;
          const visible = value.slice(0, limit);
          const hiddenCount = value.length - visible.length;
          return (
            <>
              {visible.map((option, index) => {
                const { key, ...itemProps } = getItemProps({ index });
                return (
                  <Chip
                    {...itemProps}
                    key={key}
                    label={option.label}
                    color="secondary"
                    deleteIcon={<BackspaceIcon />}
                    sx={chipSx}
                  />
                );
              })}
              {hiddenCount > 0 && (
                <Typography variant="caption" sx={{ flexShrink: 0, ml: 0.5 }}>
                  +{hiddenCount}
                </Typography>
              )}
            </>
          );
        }}
        label={label}
        size={size}
        disabled={disabled}
        sx={[multipleFixedHeightSx, ...combinedSx]}
      />
    );
  }

  const selectedOption = options.find((o) => o.value === rest.value);

  return (
    <CustomAutocomplete<DayOption, false, true>
      disableClearable
      formStyle={false}
      options={options}
      value={selectedOption}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      filterOptions={(opts) => opts}
      onChange={(_, newValue) => rest.onChange(newValue.value)}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          {option.label}
        </li>
      )}
      label={label}
      size={size}
      disabled={disabled}
      sx={combinedSx}
    />
  );
};

export default WeekDaySelector;
